import { inject, Injectable } from '@angular/core';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  EventType,
  IdTokenClaims,
  InteractionStatus,
  InteractionType,
  PromptValue,
  RedirectRequest,
  SsoSilentRequest,
} from '@azure/msal-browser';
import { Observable, of, throwError, timer } from 'rxjs';
import { catchError, filter, map, retry, shareReplay, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Guid } from 'guid-typescript';

import { msalConfig } from './auth-config';
import { BrainnetPrincipal } from '@v2/core/security/models/brainnetprincipal';
import { ACTIVEMEMBER_STORAGE_KEY, MemberIdentity } from '@v2/core/security/models/memberidentity';
import { Company } from '@v2/core/security/models/company';
import { ScopePermission } from '@v2/core/security/models/scopepermission';
import { Permission } from '@v2/core/security/models/permission';
import { LocalStorage } from '@v2/core/helpers/localStorage';
import { UserType } from '@v2/data/schema/userType';
import { environment } from '@env';

const CLAIMS_RETRY_COUNT = 3;
const CLAIMS_RETRY_BASE_DELAY_MS = 1000;

type IdTokenClaimsWithPolicyId = IdTokenClaims & {
  acr?: string;
  tfp?: string;
};

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  isIframe = false;

  readonly account$: Observable<AccountInfo | null>;
  readonly principal$: Observable<BrainnetPrincipal | null>;
  readonly boabaasUser$: Observable<BoabaasUser>;
  readonly isAdmin$: Observable<boolean>;

  private readonly msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly msalService = inject(MsalService);
  private readonly router = inject(Router);
  private readonly httpClient = inject(HttpClient);

  private eventHandlingInitialized = false;
  private principalSnapshot: BrainnetPrincipal | null = null;

  constructor() {
    this.account$ = this.msalBroadcastService.inProgress$.pipe(
      filter((status) => status === InteractionStatus.None),
      map(() => this.ensureActiveAccountSnapshot()),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.boabaasUser$ = this.account$.pipe(
      switchMap((acc) => {
        if (!acc) {
          return of(new BoabaasUser(null));
        }

        return this.fetchSupplierClaims().pipe(
          retry({ count: CLAIMS_RETRY_COUNT, delay: claimsRetryDelay }),
          map((claims) => new BoabaasUser(acc, claims)),
          catchError(() => {
            this.logout();
            return of(new BoabaasUser(null));
          }),
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.principal$ = this.boabaasUser$.pipe(
      map((user) => {
        const claims = user.accountInfo?.idTokenClaims;
        if (!claims) {
          this.principalSnapshot = null;
          return null;
        }

        const principal = new BrainnetPrincipal(claims, user.isAdmin);
        this.setActiveMember(user, principal);
        this.principalSnapshot = principal;
        return principal;
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.isAdmin$ = this.boabaasUser$.pipe(map((boabaasUser) => boabaasUser?.isAdmin ?? false));
  }

  tryGetPrincipalSnapshot(): BrainnetPrincipal | null {
    if (!this.ensureActiveAccountSnapshot()) {
      return null;
    }

    return this.principalSnapshot;
  }

  ensureActiveAccountSnapshot(): AccountInfo | null {
    const msal = this.msalService.instance;

    const activeAccount = msal.getActiveAccount();
    if (isValidAccount(activeAccount)) {
      return activeAccount;
    }

    const accounts = msal.getAllAccounts();
    for (const acc of accounts) {
      if (isValidAccount(acc)) {
        msal.setActiveAccount(acc);
        return acc;
      }
    }

    return null;
  }

  setActiveMember(
    user: BoabaasUser,
    principal: BrainnetPrincipal,
    identifier: Guid = LocalStorage.Guid(ACTIVEMEMBER_STORAGE_KEY),
  ): void {
    for (const member of user.identities) {
      if (member.identifier.toString() === identifier.toString()) {
        principal.setActiveMember(member);
        return;
      }
    }
    const firstIdentity = user.identities[0];
    if (firstIdentity) {
      LocalStorage.setGuid(ACTIVEMEMBER_STORAGE_KEY, firstIdentity.identifier);
      this.setActiveMember(user, principal, firstIdentity.identifier);
    }
  }

  fetchSupplierClaims(): Observable<GoldMemberClaims> {
    return this.httpClient
      .post<GoldClaimsResponse>(environment.v3.apiBaseUrl + '/web/v1/members/fetch-supplier-claims', {})
      .pipe(
        map((response): GoldMemberClaims => ({
          initials: response.initials ?? '',
          isFirstTimeLogin: response.isFirstTimeLogin,
          languageCode: response.languageCode,
          memberIdentities: mapToMemberIdentity(response.memberIdentities),
        })),
      );
  }

  loginRedirect(redirectStartPage?: string): void {
    const authRequest = this.msalGuardConfig.authRequest;

    const req: RedirectRequest = {
      scopes:
        authRequest && 'scopes' in authRequest && Array.isArray(authRequest.scopes) && authRequest.scopes.length > 0
          ? authRequest.scopes
          : msalConfig.scopes,
      ...(authRequest ? authRequest : {}),
      ...(redirectStartPage ? { redirectStartPage } : {}),
    };

    this.msalService.loginRedirect(req);
  }

  /**
   * The Authentication Boundary's single entry point: owns MSAL event orchestration
   * (account added/removed, login-success policy routing, login-failure forgot-password)
   * and the post-redirect handoff. Invoked once at application start.
   */
  handleRedirectAndEvents(): Observable<AuthenticationResult | null> {
    if (!this.eventHandlingInitialized) {
      this.eventHandlingInitialized = true;
      this.isIframe = window !== window.parent && !window.opener;
      this.watchAccountRemoval();
      this.watchLoginSuccess();
      this.watchAuthFailure();
    }

    return this.msalService.handleRedirectObservable({ navigateToLoginRequestUrl: false });
  }

  private watchAccountRemoval(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.LOGOUT_SUCCESS,
        ),
      )
      .subscribe(() => {
        if (this.msalService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        }
      });
  }

  private watchLoginSuccess(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS))
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        const idtoken = payload.idTokenClaims as IdTokenClaimsWithPolicyId;

        if (matchesPolicy(idtoken, msalConfig.policies.signUpSignIn.name)) {
          this.msalService.instance.setActiveAccount(payload.account);
        }

        /**
         * For the purpose of setting an active account for UI update, we want to consider only the auth response resulting
         * from SUSI flow. "acr" claim in the id token tells us the policy (NOTE: newer policies may use the "tfp" claim instead).
         * To learn more about B2C tokens, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/tokens-overview
         */
        if (matchesPolicy(idtoken, msalConfig.policies.editProfile.name)) {
          // retrieve the account from initial sign-in to the app
          const originalSignInAccount = this.msalService.instance
            .getAllAccounts()
            .find(
              (account: AccountInfo) =>
                account.idTokenClaims?.oid?.toLowerCase() === idtoken.oid?.toLowerCase() &&
                account.idTokenClaims?.sub?.toLowerCase() === idtoken.sub?.toLowerCase() &&
                matchesPolicy(
                  account.idTokenClaims as IdTokenClaimsWithPolicyId,
                  msalConfig.policies.signUpSignIn.name,
                ),
            );

          const signUpSignInFlowRequest: SsoSilentRequest = {
            authority: msalConfig.policies.signUpSignIn.authority,
            account: originalSignInAccount,
          };

          // silently login again with the signUpSignIn policy
          this.msalService.ssoSilent(signUpSignInFlowRequest);
        }

        /**
         * Below we are checking if the user is returning from the reset password flow.
         * If so, we will ask the user to reauthenticate with their new password.
         */
        if (matchesPolicy(idtoken, msalConfig.policies.resetPassword.name)) {
          this.msalService.loginRedirect({
            authority: msalConfig.policies.signUpSignIn.authority,
            scopes: msalConfig.scopes,
            prompt: PromptValue.LOGIN, // force user to reauthenticate with their new password
          } as RedirectRequest);
        }
      });
  }

  private watchAuthFailure(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE))
      .subscribe((result: EventMessage) => {
        // Check for forgot password error.
        // Learn more about AAD error codes at https://docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
        if (isForgotPasswordError(result.error)) {
          this.msalService.loginRedirect({
            authority: msalConfig.policies.resetPassword.authority,
            scopes: [],
          } as RedirectRequest);
        }
      });
  }

  getToken(): Observable<string> {
    return this.msalBroadcastService.msalSubject$.pipe(
      map((eventMessage) => {
        if (eventMessage.eventType === 'msal:acquireTokenSuccess' && eventMessage.payload) {
          const request = eventMessage.payload as AuthenticationResult;
          return request.accessToken;
        }
        return null;
      }),
      filter((accessToken): accessToken is string => !!accessToken),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  logout(): void {
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      this.msalService.logoutPopup({ account: this.msalService.instance.getActiveAccount() });
    } else {
      this.msalService.logoutRedirect({ account: this.msalService.instance.getActiveAccount() });
    }
  }

  returnToHome(): void {
    this.router.navigateByUrl('/');
  }
}

export class BoabaasUser {
  readonly identities: MemberIdentity[];

  constructor(
    public accountInfo: AccountInfo | null,
    private readonly claims: GoldMemberClaims | null = null,
  ) {
    this.identities = claims?.memberIdentities ?? [];
  }

  get languageCode(): string | null {
    return this.claims?.languageCode ?? null;
  }

  get initials(): string {
    const value = this.claims?.initials;
    if (value) return value;
    return this.isAdmin ? 'PA' : '?';
  }

  get fullName(): string | undefined {
    return this.accountInfo?.name;
  }

  get companies(): Company[] | null {
    const { identities } = this;
    if (!identities) return null;

    const companies: Company[] = [];
    for (const identity of identities) {
      for (const company of identity.companies) {
        companies.push(company);
      }
    }
    return companies;
  }

  get isAdmin(): boolean {
    const isAdmin = this.getClaim('extension_IsAdmin');
    if (isAdmin === true) return true;

    if (environment.isProduction) return false;

    const email = this.getClaim('email') as string;
    if (!email) return false;
    return email == 'portaladmin@magnitglobal.com';
  }

  get hasAgreedToEula(): boolean {
    return (this.getClaim('extension_HasAgreedToEula') as boolean) ?? false;
  }

  private getClaim(key: string): unknown {
    const idTokenClaims = this.accountInfo?.idTokenClaims;
    if (!idTokenClaims) return null;
    return idTokenClaims[key];
  }
}

export function isValidAccount(account: AccountInfo | null): boolean {
  const exp = account?.idTokenClaims?.exp || 0;
  if (exp * 1000 < Date.now()) return false;
  return msalConfig.clientId == account?.idTokenClaims?.aud;
}

function isTransientHttpError(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) return false;
  return error.status === 0 || error.status === 408 || error.status === 429 || error.status >= 500;
}

function claimsRetryDelay(error: unknown, retryCount: number): Observable<number> {
  if (!isTransientHttpError(error)) return throwError(() => error);
  return timer(CLAIMS_RETRY_BASE_DELAY_MS * 2 ** (retryCount - 1));
}

function matchesPolicy(idtoken: IdTokenClaimsWithPolicyId | undefined, policyName: string | undefined): boolean {
  if (!idtoken || !policyName) return false;
  const name = policyName.toLowerCase();
  return idtoken.acr?.toLowerCase() === name || idtoken.tfp?.toLowerCase() === name;
}

// v5 shape: verified against the installed @azure/msal-common (16.11.0, the version msal-browser
// 5.16.0 depends on) by constructing a real ServerError — AuthError.errorMessage holds the raw
// server error_description (e.g. "AADB2C90118: ..."), which is where the AADB2C sub-error text
// actually lands. error.message happens to also contain it (composed as `${errorCode}: ${errorMessage}`
// in this version) but that composition isn't part of the documented contract, so read errorMessage.
function isForgotPasswordError(error: EventMessage['error']): boolean {
  const errorMessage = (error as { errorMessage?: string; message?: string } | null)?.errorMessage ?? error?.message;
  return typeof errorMessage === 'string' && errorMessage.indexOf('AADB2C90118') > -1;
}

export interface GoldMemberClaims {
  languageCode: 'en' | 'nl';
  initials: string;
  isFirstTimeLogin: boolean;
  memberIdentities: MemberIdentity[] | null;
}

interface GoldClaimsResponse {
  languageCode: 'en' | 'nl';
  initials: string | null;
  isFirstTimeLogin: boolean;
  memberIdentities: GoldMemberIdentity[] | null;
}

interface GoldMemberIdentity {
  supplierName: string;
  memberUuid: string;
  personReferenceId: string;
  supplierReferenceId: string;
  supplierContactReferenceId: string | null;
  professionalReferenceId: string | null;
  userType: UserType;
  permissions: Record<string, Permission[]> | null;
}

function mapToMemberIdentity(gold?: GoldMemberIdentity[] | null): MemberIdentity[] | null {
  if (!gold) {
    return null;
  }
  const activemember = localStorage.getItem(ACTIVEMEMBER_STORAGE_KEY);

  return (
    gold.map((src) => {
      const memberIdentity: MemberIdentity = {
        identifier: Guid.parse(src.memberUuid),
        companies: [
          {
            contactId: Guid.parse(src.supplierContactReferenceId ?? Guid.EMPTY),
            identifier: Guid.parse(src.memberUuid ?? Guid.EMPTY),
            name: src.supplierName,
            scopePermissions: Array.prototype.concat(
              ...Object.entries(src.permissions ?? {}).map(([key, values]) => {
                const permissions = values.map((v) => {
                  return new ScopePermission(key, v);
                });

                return permissions;
              }),
            ),
            roles: [],
          },
        ],
        userType: src.userType,
        professionalId: src.professionalReferenceId,
        supplierContactId: src.supplierContactReferenceId,
        customerContactId: Guid.EMPTY,
        isActive: src.memberUuid === activemember,
      };

      return memberIdentity;
    }) ?? null
  );
}
